import React, { useMemo } from 'react';
import { CharacterRole, GameState, PlanetType, PlayerId, StarSystem } from '../types';
import { PLANET_TYPE_DEFAULTS, getPlanetTypeDefaults, getPlanetOverrideBySystem } from '../shared/planetConfig';

type Props = {
  gameState: GameState;
  myPlayerId: PlayerId | null;
};

// Mirror server-side economy constants for display consistency
const ADMIN_BONUS_PER_LEVEL = 0.05; // +5% per Admin level
const POPULATION_GROWTH_RATE = 0.02; // 2% per tick

const INCOME_RATES = PLANET_TYPE_DEFAULTS; // keep source of truth in planetConfig

function getIncomeMultipliers(gameState: GameState) {
  let credits = 1;
  let influence = 1;
  for (const law of gameState.senate.activeLaws) {
    if (law.effect.kind === 'IncomeMultiplier') {
      if (typeof law.effect.creditsMultiplier === 'number') credits *= law.effect.creditsMultiplier;
      if (typeof law.effect.influenceMultiplier === 'number') influence *= law.effect.influenceMultiplier;
    }
  }
  return { credits, influence };
}

function planetTypeLabel(p: PlanetType): string {
  return String(p).replace('_', ' ');
}

export default function EconomyScreen({ gameState, myPlayerId }: Props) {
  const me = myPlayerId ? gameState.players.find(p => p.id === myPlayerId) : null;
  const myWorlds = useMemo(
    () => gameState.systems.filter(s => s.owner === myPlayerId),
    [gameState.systems, myPlayerId]
  );

  const incomeMults = getIncomeMultipliers(gameState);

  type WorldRow = {
    system: StarSystem;
    planetType: PlanetType;
    population: number;
    maxPopulation: number;
    creditsPerTick: number;
    influencePerTick: number;
    popGrowthPerTick: number;
    hasShipyard: boolean;
    governorBonusPct?: number;
  };

  const rows: WorldRow[] = myWorlds.map(system => {
    const d = getPlanetTypeDefaults(system.planetType);
    let adminMult = 1;
    let govBonusPct: number | undefined;
    if (
      me &&
      me.character.role === CharacterRole.Governor &&
      me.character.assignment === system.id
    ) {
      const bonus = (me.character.admin ?? 0) * ADMIN_BONUS_PER_LEVEL;
      adminMult += bonus;
      govBonusPct = Math.round(bonus * 100);
    }

    const override = getPlanetOverrideBySystem(system);
    const creditsBonus = 1 + ((override?.creditsBonusPct ?? 0) / 100);
    const influenceBonus = 1 + ((override?.influenceBonusPct ?? 0) / 100);
    const baseCredits = system.population * d.creditsPerPop;
    const baseInfluence = system.population * d.influencePerPop;
    const creditsPerTick = Math.floor(baseCredits * adminMult * incomeMults.credits * creditsBonus);
    const influencePerTick = Math.floor(baseInfluence * adminMult * incomeMults.influence * influenceBonus);
    const growth = Math.min(
      system.population * POPULATION_GROWTH_RATE,
      Math.max(0, system.maxPopulation - system.population)
    );

    return {
      system,
      planetType: system.planetType,
      population: system.population,
      maxPopulation: system.maxPopulation,
      creditsPerTick,
      influencePerTick,
      popGrowthPerTick: growth,
      hasShipyard: system.hasShipyard,
      governorBonusPct: govBonusPct,
    };
  });

  const totals = rows.reduce(
    (acc, r) => {
      acc.credits += r.creditsPerTick;
      acc.influence += r.influencePerTick;
      acc.population += r.population;
      acc.growth += r.popGrowthPerTick;
      return acc;
    },
    { credits: 0, influence: 0, population: 0, growth: 0 }
  );

  // Sort by highest credits by default for usefulness
  rows.sort((a, b) => b.creditsPerTick - a.creditsPerTick);

  return (
    <div className="screen-panel economy" onClick={e => e.stopPropagation()}>
      <div className="screen-header">
        <div className="screen-title">Economy</div>
      </div>
      <div className="screen-content">
        {!me ? (
          <div style={{ opacity: 0.85 }}>Connect to view your empire's economy.</div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="row between">
                <div>
                  <strong>{rows.length}</strong> controlled worlds
                </div>
                <div className="row gap">
                  <div className="pill">💰 +{totals.credits} /tick</div>
                  <div className="pill">✨ +{totals.influence} /tick</div>
                  <div className="pill" title="Total population across your worlds">👥 {totals.population.toFixed(1)}M</div>
                  <div className="pill" title="Aggregate population growth per tick">⬆ {totals.growth.toFixed(2)}M/t</div>
                </div>
              </div>
            </div>

            <div className="economy-table">
              <div className="eco-row eco-head">
                <div>World</div>
                <div>Type</div>
                <div className="right">Population</div>
                <div className="right">Growth/t</div>
                <div className="right">💰/t</div>
                <div className="right">✨/t</div>
                <div className="right">Facility</div>
              </div>
              {rows.length === 0 && (
                <div className="card" style={{ textAlign: 'center', opacity: 0.85 }}>No controlled worlds yet.</div>
              )}
              {rows.map(r => (
                <div key={r.system.id} className="eco-row">
                  <div>
                    <div style={{ fontWeight: 700 }}>{r.system.name}</div>
                    {typeof r.governorBonusPct === 'number' && (
                      <div style={{ fontSize: 12, color: '#7af2b2' }}>Governor +{r.governorBonusPct}%</div>
                    )}
                  </div>
                  <div style={{ textTransform: 'capitalize' }}>{planetTypeLabel(r.planetType)}</div>
                  <div className="right">{r.population.toFixed(1)}M / {r.maxPopulation}M</div>
                  <div className="right">+{r.popGrowthPerTick.toFixed(2)}M</div>
                  <div className="right">+{r.creditsPerTick}</div>
                  <div className="right">+{r.influencePerTick}</div>
                  <div className="right">{r.hasShipyard ? 'Shipyard' : '—'}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


